import * as Yup from "yup";
import { PayoutCurrency } from "../redux/features/payment/payment.types";
import { currencyShorts } from "./constants";
import {
  formatToCurrency,
  isPhoneNumberValid,
  parseDateStringToDate,
  parsePhoneNumber,
  parseStringToNumber,
  parseTimeToDate,
} from "./helpers";

export const SignInSchema = Yup.object({
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

export const SalesSignupSchema = Yup.object({
  password: Yup.string().required("Password is required"),
});

export const SignUpSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
});

export const VerifySignUpSchema = Yup.object({
  code: Yup.string().required("Code is required"),
});

export const ForgotPasswordSchema = Yup.object({
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
});

export const ResetPasswordSchema = Yup.object({
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .required("You must confirm your password")
    .oneOf([Yup.ref("password")], "Passwords do not match"),
});

export const EventDetailsSchema = Yup.object({
  name: Yup.string().required("Event name is required"),
  description: Yup.string().required("Description is required"),
  category: Yup.string().required("Choose a category"),
  timezone: Yup.string().required("Choose a timezone"),
  placeId: Yup.string().optional(),
  longitude: Yup.string().optional(),
  latitude: Yup.string().optional(),
  location: Yup.string()
    .required("Required")
    .when("type", ([type], schema) => {
      return type === "virtual"
        ? schema.url("Enter a valid url")
        : schema.required();
    }),
  startDate: Yup.string()
    .required("Start date is required")
    .test("is-not-past", "Start date cannot be in the past", function (value) {
      return (
        !value ||
        new Date(value).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)
      );
    }),
  endDate: Yup.string()
    .required("End date is required")
    .test(
      "is-greater",
      "End date must be later than start date",
      function (value) {
        const { startDate } = this.parent;
        return (
          !startDate ||
          !value ||
          new Date(value).setHours(0, 0, 0, 0) >=
            new Date(startDate).setHours(0, 0, 0, 0)
        );
      },
    ),
  startTime: Yup.string()
    .required("Start time is required")
    .test("is-not-past", "Start time cannot be in the past", function (value) {
      const { startDate } = this.parent;

      const fullStartDate = parseDateStringToDate(
        `${startDate} ${value}`,
        "MMMM DD, YYYY h:mm A",
        true,
      );

      return (
        !startDate || !value || !fullStartDate || fullStartDate >= new Date()
      );
    }),
  endTime: Yup.string()
    .required("End time is required")
    .test(
      "is-greater",
      "End time must be later than start time",
      function (value) {
        const { startTime, startDate, endDate } = this.parent;

        if (
          parseTimeToDate(value) !== null &&
          parseTimeToDate(startTime) !== null &&
          parseDateStringToDate(startDate, "MMMM DD, YYYY", true) !== null &&
          parseDateStringToDate(endDate, "MMMM DD, YYYY", true) !== null
        ) {
          return (
            (startTime &&
              value &&
              parseTimeToDate(value)! > parseTimeToDate(startTime)! &&
              endDate! === startDate!) ||
            (startTime &&
              value &&
              parseDateStringToDate(endDate, "MMMM DD, YYYY", true)! >
                parseDateStringToDate(startDate, "MMMM DD, YYYY", true)!)
          );
        } else {
          return false;
        }
      },
    ),
  image: Yup.string(),
  type: Yup.string().required("Event type is required"),
  status: Yup.string().required("Event status is required"),
  customSlug: Yup.boolean(),
  slug: Yup.string()
    .matches(
      /^[A-Za-z0-9-]+$/,
      "Only letters, numbers, and hyphens are allowed",
    )
    .transform((val) => (val as string).trim().toLowerCase())
    .notOneOf(
      ["find-events", "pricing", "404", "legal", "for-organizers"],
      "This URL is taken",
    )
    .when("customSlug", ([customSlug], schema) => {
      return customSlug
        ? schema.required("Slug is required")
        : schema.notRequired();
    }),
  placeholderLocation: Yup.string().when(
    "hideLocation",
    ([hideLocation], schema) => {
      return hideLocation ? schema.required("Required") : schema.notRequired();
    },
  ),
});

export const EventTicketSchema = (minQuantity: number) =>
  Yup.object({
    type: Yup.string().required("Ticket type is required"),
    name: Yup.string().required("Ticket name is required"),
    description: Yup.string(),
    price: Yup.string().when("type", ([type], schema) => {
      return type === "paid"
        ? schema
            .required("Price is required")
            .test("is-valid-amount", "Invalid amount", function (value) {
              return parseStringToNumber(value) !== null;
            })
            .test("is-greater-than-zero", function (value) {
              const { path, createError, parent } = this;

              const currency = parent.currency as PayoutCurrency; // This gets the actual value of the "currency" field
              const minAmount = currency === "NGN" ? 1000 : 5;
              const formattedMin = formatToCurrency(minAmount, {
                currency: currencyShorts[currency ?? "NGN"],
              });

              const parsed = parseStringToNumber(value);

              if (parsed === null || parsed < minAmount) {
                return createError({
                  path,
                  message: `Ticket price should be at least ${formattedMin}`,
                });
              }

              return true;
            })
        : schema.notRequired();
    }),
    quantityType: Yup.string().required("Quantity type is required"),
    quantity: Yup.string().when("quantityType", ([quantityType], schema) => {
      return quantityType === "limited"
        ? schema
            .required("Quantity is required")
            .test("is-valid-amount", "Invalid quantity", function (value) {
              return parseStringToNumber(value) !== null;
            })
            .test(
              "is-greater-than-zero",
              "Ticket quantity must be greater than 0",
              function (value) {
                return (
                  parseStringToNumber(value) !== null &&
                  parseStringToNumber(value)! > 0
                );
              },
            )
            .test(
              "is-greater-than-min",
              `Ticket quantity cannot be less than amount purchased`,
              function (value) {
                const parsed = parseStringToNumber(value);
                return parsed !== null && parsed >= minQuantity;
              },
            )
        : schema.notRequired();
    }),
    currency: Yup.string().when("type", ([type], schema) => {
      return type === "paid"
        ? schema.required("Curency is required")
        : schema.notRequired();
    }),
  });

export const PurchaseTicketSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  phoneNumber: Yup.string()
    .test("is-valid-phone", `Enter a valid phone number`, (value) => {
      const parsed = parsePhoneNumber(value || "")?.country ?? "NG";
      return isPhoneNumberValid(value || "", parsed);
    })
    .required("Phone number is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  hasGuests: Yup.boolean().required(),
  guests: Yup.array()
    .when("hasGuests", (hasGuests, schema) => {
      return hasGuests[0]
        ? schema.of(
            Yup.object().shape({
              firstName: Yup.string().required("First name is required"),
              lastName: Yup.string().required("Last name is required"),
              email: Yup.string()
                .email("Email is not a valid email")
                .required("Email is required"),
              title: Yup.string(),
              ticketId: Yup.string(),
            }),
          )
        : schema.of(
            Yup.object().shape({
              firstName: Yup.string(),
              lastName: Yup.string(),
              email: Yup.string(),
              title: Yup.string(),
              ticketId: Yup.string(),
            }),
          );
    })

    .min(0),
});

export const NGNPayoutSchema = Yup.object({
  bankName: Yup.string().required("Bank name is required"),
  accountNumber: Yup.string().required("Account number is required"),
  bankCode: Yup.string().required("Bank name is required"),
  accountName: Yup.string().required("Account namme is required"),
  currency: Yup.string().required("Currency is required"),
});

export const USDPayoutSchema = Yup.object({
  accountName: Yup.string().required("Account name is required"),
  swiftRoutingNumber: Yup.string().required("Routing number is required"),
  accountNumber: Yup.string().required("Account number is required"),
  confirm: Yup.string()
    .required("You must confirm the account number")
    .oneOf([Yup.ref("accountNumber")], "Account numbers do not match"),
  currency: Yup.string().required("Currency is required"),
});

export const CADPayoutSchema = Yup.object({
  interacName: Yup.string().required("Account name is required"),
  interacEmail: Yup.string()
    .email("Enter a valid email address")
    .required("Interac email address is required"),
  currency: Yup.string().required("Currency is required"),
});

export const AddPayoutSchema = Yup.object({
  currency: Yup.string().required("Currency is required"),
});

export const UpdateProfileSchema = Yup.object({
  firstName: Yup.string(),
  lastName: Yup.string(),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  isOrganization: Yup.boolean().optional(),
  phoneNumber: Yup.string().when(
    "isOrganization",
    ([isOrganization], schema) => {
      return (isOrganization ?? false)
        ? schema.optional()
        : schema
            .required("Phone number is required")
            .test("is-valid-phone", `Enter a valid phone number`, (value) => {
              const parsed = parsePhoneNumber(value || "")?.country ?? "NG";
              return isPhoneNumberValid(value || "", parsed);
            });
    },
  ),
});

export const SettingPageUpdateProfileSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .test("is-valid-phone", `Enter a valid phone number`, (value) => {
      const parsed = parsePhoneNumber(value || "")?.country ?? "NG";
      return isPhoneNumberValid(value || "", parsed);
    })
    .required("Phone number is required"),
});

export const DiscountSchema = Yup.object({
  name: Yup.string().required("Discount name is required"),
  type: Yup.string().required("Discount type is required"),
  value: Yup.string().when("type", ([type], schema) => {
    if (type === "amount") {
      return schema
        .required(`${type} is required`)
        .test("is-valid-amount", `Invalid ${type}`, function (value) {
          return parseStringToNumber(value) !== null;
        })
        .test(
          "is-greater-than-zero",
          `${type} must be greater than 0`,
          function (value) {
            return (
              parseStringToNumber(value) !== null &&
              parseStringToNumber(value)! > 0
            );
          },
        );
    } else if (type === "percentage") {
      return schema
        .required(`${type} is required`)
        .test("is-valid-amount", `Invalid ${type}`, function (value) {
          return parseStringToNumber(value) !== null;
        })
        .test(
          "is-greater-than-zero",
          `${type} must be greater than 0`,
          function (value) {
            return (
              parseStringToNumber(value) !== null &&
              parseStringToNumber(value)! > 0
            );
          },
        )
        .test(
          "is-not-more-than-hundred",
          `${type} cannot exceed 100%`,
          function (value) {
            return (
              parseStringToNumber(value) !== null &&
              parseStringToNumber(value)! <= 100
            );
          },
        );
    } else {
      return schema.notRequired();
    }
  }),
  quantityType: Yup.string().required("Quantity type is required"),
  quantity: Yup.string().when("quantityType", ([quantityType], schema) => {
    return quantityType === "limited"
      ? schema
          .required("Quantity is required")
          .test("is-valid-amount", "Invalid quantity", function (value) {
            return parseStringToNumber(value) !== null;
          })
          .test(
            "is-greater-than-zero",
            "Quantity must be greater than 0",
            function (value) {
              return (
                parseStringToNumber(value) !== null &&
                parseStringToNumber(value)! > 0
              );
            },
          )
      : schema.notRequired();
  }),
});

export const OrganisationInformationSchema = Yup.object({
  isOrganisation: Yup.boolean().required(),
  orgPhoneNumber: Yup.string()
    .test("is-valid-phone", `Enter a valid phone number`, (value) => {
      const parsed = parsePhoneNumber(value || "")?.country ?? "NG";
      return isPhoneNumberValid(value || "", parsed);
    })
    .required("Organization phone number is required"),
  // phoneNumber: Yup.string()
  // .required("Phone number is required")
  // .matches(/^[+]?[0-9]{10,15}$/, "Phone number must be a valid number"),
  name: Yup.string().required("Organization name is required"),
  orgEmail: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  logo: Yup.string().optional(),
  website: Yup.string().url().optional(),
});

export const FlagEventSchema = Yup.object({
  reason: Yup.string().required("Reason is required"),
});

export const DuplicateEventSchema = Yup.object({
  name: Yup.string().required("Event name is required"),
});

export const AddSocialsSchema = Yup.object({
  facebook: Yup.string().url("Enter a valid link"),
  twitter: Yup.string().url("Enter a valid link"),
  instagram: Yup.string().url("Enter a valid link"),
  tiktok: Yup.string().url("Enter a valid link"),
});

export const ChangePasswordSchema = Yup.object({
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("New Password is required"),
  confirmPassword: Yup.string()
    .required("You must confirm your new password")
    .oneOf([Yup.ref("password")], "Passwords do not match"),
  oldPassword: Yup.string().required("Password is required"),
});

export const SendEmailBlastSchema = Yup.object({
  recipients: Yup.string().required("Recipients are required"),
  message: Yup.string().required("Message is required"),
});

export const InviteCoOrganizerSchema = Yup.object({
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  role: Yup.string().required("Role is required"),
});

export const HolidaySchema = Yup.object().shape({
  name: Yup.string().required("Holiday name is required"),
  holidayDate: Yup.string().required("Holiday date is required"),
  nextWorkingDay: Yup.string()
    .required("Next working day is required")
    .test(
      "is-after-holiday",
      "Next working day must be after the holiday date",
      function (value) {
        const { holidayDate } = this.parent;
        if (!holidayDate || !value) return true;

        const holiday = new Date(holidayDate);
        const nextDay = new Date(value);

        return nextDay.getTime() > holiday.getTime();
      },
    ),
});

export const AddAdminSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  role: Yup.string().required("Choose a role"),
});

export const TwoFaSchema = Yup.object()
  .shape({
    backupCode: Yup.string(),
    twoFaCode: Yup.string(),
  })
  .test(
    "one-of-required",
    "You must provide either a backup code or a 2FA code",
    function (value) {
      return Boolean(value.backupCode || value.twoFaCode);
    },
  );

export const VerificationSchema = Yup.object({
  email: Yup.string()
    .email("Email is not a valid email")
    .required("Email is required"),
  twoFaCode: Yup.string().required("Code is required"),
});

export const ManageSalesAgentSchema = Yup.object({
  email: Yup.string().required("Email is required"),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  ngn: Yup.string().required("NGN commission amount is required"),
  cad: Yup.string().required("CAD commission amount is required"),
});
